// pages/ExpensePurchaseOrder/MpcEpoViewer.js
//
// Legacy menu: "MPC EPO Viewer" — under EPO. (Also serves Accounting EPO
// Viewer via the R30 title/subtitle props pattern.)
//
// R41 — replaced the radio-button facet search bar with the per-column
// filter row (Epicor "Customer" page UX). Each column header now has a
// filter input directly beneath it; users type to filter what's loaded
// on the current page. Status tabs (All/Pending/Approved/Denied) stay
// because that's a different axis (server-side type filter, not search).
//
// Reuses: GET /api/Epo/all (R18a search left in place but no longer driven
// from this page; will still work if called via URL params).

import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import useTableSort from "../../Components/Common/DashboardKit/useTableSort";
import SortableTh from "../../Components/Common/DashboardKit/SortableTh";
import { useSearchParams } from "react-router-dom";
import exportRowsToCsv, { exportAllPagesToCsv, fetchAllPages } from "../../Components/Common/DashboardKit/exportCsv";
import {
    Container, Card, CardBody, Input, Spinner, Badge,
    Pagination, PaginationItem, PaginationLink, Alert,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
    DashboardPageHeader,
    EmptyState,
    ResponsiveTabs,
    ColumnFilterRow,
    applyColumnFilters,
    // R44/R46 — applyColumnFilters dropped (server filters now);
    //         useDebouncedValue dropped (text inputs now commit on Enter/blur).
} from "../../Components/Common/DashboardKit";
import api from "../../api";
import useEpoReviewModal from "../../hooks/useEpoReviewModal";

const STATUS_TABS = [
    { key: "ALL", label: "All" },
    { key: "PENDING", label: "Pending" },
    { key: "APPROVED", label: "Approved" },
    { key: "DENIED", label: "Denied / Void" },
];

const fmtMoney = (v) => {
    const n = typeof v === "number" ? v : parseFloat(v);
    if (!isFinite(n)) return "—";
    return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
};
const fmtDate = (d) => {
    if (!d) return "—";
    try { return new Date(d).toLocaleDateString(); } catch { return "—"; }
};
const pick = (r, ...keys) => {
    for (const k of keys) {
        if (r != null && r[k] !== undefined && r[k] !== null) return r[k];
    }
    return undefined;
};

const StatusBadge = ({ status }) => {
    const s = String(status || "").toUpperCase();
    const color =
        s === "APPROVED" ? "success" :
            s === "PENDING" ? "warning" :
                s === "DENIED" || s === "VOID" ? "danger" : "secondary";
    return <Badge color={color} pill style={{ fontSize: 11 }}>{s || "—"}</Badge>;
};

const MpcEpoViewer = ({
    title = "MPC EPO Viewer",
    subtitle = "Last 1000 EPOs across the company",
} = {}) => {
    const navigate = useNavigate();
    // Owner 2026-10-13: the list showed an EPO as DRAFT while the review window
    // said APPROVED. The list was only fetched on tab/page/filter changes, so a
    // row kept the status it had when the page first loaded. The list now
    // refreshes quietly after an action in the review window, when the window
    // closes, and when the person comes back to this browser tab.
    const refreshRef = useRef(() => {});
    const epoReview = useEpoReviewModal({ permissions: {}, onActionDone: () => refreshRef.current() });

    // Round 27 — the status tab lives in the URL (?status=PENDING): links
    // are shareable and dashboard tiles can deep-link a pre-filtered view.
    const [searchParams, setSearchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(
        () => (searchParams.get("status") || "ALL").toUpperCase()
    );
    useEffect(() => {
        const cur = (searchParams.get("status") || "ALL").toUpperCase();
        if (cur !== activeTab) {
            setSearchParams(activeTab === "ALL" ? {} : { status: activeTab });   // Owner 2026-10-12: Back returns to the previous tab
        }
    }, [activeTab]);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // R41 — per-column filter state. Single object keyed by column id.
    // R44/R46 — columnFilters is the committed filter state.
    //         Text inputs in ColumnFilterRow commit on Enter or focus-out,
    //         so we can drive the server fetch off this state directly.
    const [columnFilters, setColumnFilters] = useState({});

    // Pagination.
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Round 31 — "all rows" mode: first sort click loads the COMPLETE tab
    // dataset (same page-walking as Export all). After that, sorting,
    // column filters, and paging are instant and operate on ALL rows.
    const [allRows, setAllRows] = useState(null);
    const [loadingAll, setLoadingAll] = useState(false);
    const loadAllRows = async () => {
        if (loadingAll || allRows) return;
        setLoadingAll("Loading all…");
        try {
            const fetched = await fetchAllPages({
                onProgress: (n, t) => setLoadingAll(`Loading all ${n} / ${t}…`),
                fetchPage: async (pageNum, sizeNum) => {
                    const params = { page: pageNum, pageSize: sizeNum, type: activeTab };
                    const { data } = await api.get("Epo/all", { params });
                    const list = Array.isArray(data?.results) ? data.results
                        : Array.isArray(data?.Results) ? data.Results : [];
                    return { rows: list, total: data?.Total ?? data?.total ?? 0 };
                },
            });
            setAllRows(fetched);
            setPage(1);
        } catch (e) { /* keep server mode on failure */ }
        setLoadingAll(false);
    };
    // Round 29 — full CSV export: same endpoint + filters as the table,
    // looped across all pages (the visible page is just a window).
    const [exporting, setExporting] = useState(false);
    const handleExportAll = async () => {
        try {
            setExporting("Preparing…");
            await exportAllPagesToCsv({
                filename: `epos-all-${activeTab.toLowerCase()}.csv`,
                columns: columns.filter((c) => c.type !== "none"),
                total,
                onProgress: (n, t) => setExporting(`Exporting ${n} / ${t}…`),
                fetchPage: async (pageNum, sizeNum) => {
                    const params = { page: pageNum, pageSize: sizeNum, type: activeTab };
                    for (const [k, v] of Object.entries(columnFilters || {})) {
                        if (v == null || v === "") continue;
                        params[`filter${k[0].toUpperCase()}${k.slice(1)}`] = v;
                    }
                    const { data } = await api.get("Epo/all", { params });
                    const list = Array.isArray(data?.results) ? data.results
                        : Array.isArray(data?.Results) ? data.Results : [];
                    return { rows: list, total: data?.Total ?? data?.total ?? 0 };
                },
            });
        } catch (e) {
            setExporting(false);
            return;
        }
        setExporting(false);
    };


    // R44 — load() now takes filters too and maps non-empty values to
    // filter_* query params. Backend ANDs them with existing search/type.
    const load = useCallback((tabVal, pageNum, sizeNum, filters) => {
        let alive = true;
        setLoading(true);
        setError(null);
        const params = {
            type: tabVal,
            page: pageNum ?? 1,
            pageSize: sizeNum ?? 25,
        };
        // R44 — forward per-column filters as filter_<id> query params.
        for (const [k, v] of Object.entries(filters || {})) {
            if (v == null || v === "") continue;
            // R44 — send as camelCase filter<Col>; .NET binds case-insensitively.
            params[`filter${k[0].toUpperCase()}${k.slice(1)}`] = v;
        }
        api.get("Epo/all", { params })
            .then(({ data }) => {
                if (!alive) return;
                const list = Array.isArray(data?.results) ? data.results
                    : Array.isArray(data?.Results) ? data.Results
                        : [];
                setRows(list);
                const t = data?.Total ?? data?.total ?? 0;
                const tp = data?.TotalPages ?? data?.totalPages ?? 1;
                const sp = data?.Page ?? data?.page ?? (pageNum ?? 1);
                setTotal(t);
                setTotalPages(Math.max(1, tp));
                setPage(sp);
            })
            .catch((e) => {
                if (!alive) return;
                setError(e?.response?.data?.Message || e?.message || "Failed to load.");
                setRows([]);
            })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    // R44 — tab change resets filters + paging.
    useEffect(() => {
        setColumnFilters({});
        setPage(1);
        setAllRows(null); // Round 31 — tab is a server param; all-mode reloads on demand
        return load(activeTab, 1, pageSize, {});
        // eslint-disable-next-line
    }, [activeTab]);

    // R44 — reload when debounced filters change. Resets to page 1.
    useEffect(() => {
        setPage(1);
        if (allRows) return; // Round 31 — all-mode filters client-side, no refetch
        return load(activeTab, 1, pageSize, columnFilters);
        // eslint-disable-next-line
    }, [columnFilters]);

    // const goToPage = (n) => {
    //     const t = Math.max(1, Math.min(totalPages, n));
    //     if (t === page) return;
    //     load(activeTab, t, pageSize, columnFilters);
    // };
    // const changePageSize = (sz) => {
    //     const s2 = Number(sz) || 25;
    //     setPageSize(s2);
    //     load(activeTab, 1, s2, columnFilters);
    // };
    // Replace goToPage:
    const goToPage = (n) => {
        const t = Math.max(1, Math.min(effTotalPages, n));
        if (t === page) return;
        setPage(t); // let useEffect fire load
    };

    // Replace changePageSize:
    const changePageSize = (sz) => {
        const s2 = Number(sz) || 25;
        setPageSize(s2);
        setPage(1);
    };

    // Add a useEffect that owns ALL fetches:
    useEffect(() => {
        return load(activeTab, page, pageSize, columnFilters);

    }, [page, pageSize]);

    // Quiet refresh of the rows on screen: no spinner, same tab, page and
    // filters. In "all rows" mode the fresh page is merged into the full set
    // by EPO number so sorting and the loaded rows stay as they are.
    const lastRefreshAt = useRef(0);
    const refreshQuiet = useCallback(async () => {
        lastRefreshAt.current = Date.now();
        try {
            const params = { type: activeTab, page, pageSize };
            for (const [k, v] of Object.entries(columnFilters || {})) {
                if (v == null || v === "") continue;
                params[`filter${k[0].toUpperCase()}${k.slice(1)}`] = v;
            }
            const { data } = await api.get("Epo/all", { params });
            const list = Array.isArray(data?.results) ? data.results
                : Array.isArray(data?.Results) ? data.Results : [];
            const keyOf = (r) => String(pick(r, "ePONumber", "EPONumber", "epoNumber") ?? "");
            if (allRows) {
                const fresh = new Map(list.map((r) => [keyOf(r), r]));
                setAllRows((prev) => (prev || []).map((r) => fresh.get(keyOf(r)) || r));
            } else {
                setRows(list);
                setTotal(data?.Total ?? data?.total ?? 0);
                setTotalPages(Math.max(1, data?.TotalPages ?? data?.totalPages ?? 1));
            }
        } catch (e) { /* keep what is on screen */ }
    }, [activeTab, page, pageSize, columnFilters, allRows]);
    useEffect(() => { refreshRef.current = refreshQuiet; }, [refreshQuiet]);

    // the review window closed: pick up whatever changed while it was open
    const wasOpen = useRef(false);
    useEffect(() => {
        if (wasOpen.current && !epoReview.isOpen) refreshRef.current();
        wasOpen.current = epoReview.isOpen;
    }, [epoReview.isOpen]);

    // the person comes back to this browser tab or window
    useEffect(() => {
        const onBack = () => {
            if (document.visibilityState === "hidden") return;
            if (Date.now() - lastRefreshAt.current < 15000) return;
            refreshRef.current();
        };
        window.addEventListener("focus", onBack);
        document.addEventListener("visibilitychange", onBack);
        return () => {
            window.removeEventListener("focus", onBack);
            document.removeEventListener("visibilitychange", onBack);
        };
    }, []);

    // R41 — column defs with type + accessor. Action column gets type:"none".
    const columns = useMemo(() => [
        {
            id: "epoNumber", label: "EPO #", type: "text",
            get: r => pick(r, "ePONumber", "EPONumber", "epoNumber")
        },
        {
            id: "createdDate", label: "Created", type: "text",
            get: r => fmtDate(pick(r, "createdDate", "CreatedDate"))
        },
        {
            id: "purpose", label: "Purpose", type: "text",
            get: r => pick(r, "epoPurpose", "EpoPurpose")
        },
        {
            id: "createdBy", label: "Created By", type: "text",
            get: r => pick(r, "createdBy", "CreatedBy")
        },
        {
            id: "status", label: "Status", type: "select",
            options: [
                { value: "APPROVED", label: "Approved" },
                { value: "PENDING", label: "Pending" },
                { value: "DENIED", label: "Denied" },
                { value: "VOID", label: "Void" },
            ],
            get: r => String(pick(r, "status", "Status") || "").toUpperCase()
        },
        {
            id: "approxTotal", label: "Approx. Total", type: "text",
            get: r => {
                const v = pick(r, "approxTotal", "ApproxTotal");
                return v != null ? String(v) : "";
            }
        },
        {
            id: "received", label: "Received", type: "boolean",
            get: r => !!pick(r, "received", "Received")
        },
        {
            id: "paid", label: "Paid", type: "boolean",
            get: r => !!pick(r, "paid", "Paid")
        },
        { id: "actions", label: "", type: "none" },
    ], []);

    // R44 — visibleRows removed. The server now returns already-filtered
    //        rows for the current page (the in-memory applyColumnFilters
    //        was the bug: filters only matched the 25 rows on screen).
    const visibleRowsBase = allRows
        ? applyColumnFilters(allRows, columns, columnFilters)
        : rows;
    // Round 30/31 — click-to-sort; first click also loads ALL rows so the
    // sort covers the whole dataset, not just the visible page.
    const { sorted: visibleRows, sort, toggleSort } = useTableSort(visibleRowsBase, columns);
    const onSortToggle = (id) => {
        if (!allRows && !loadingAll) loadAllRows();
        toggleSort(id);
    };
    const effTotal = allRows ? visibleRows.length : total;
    const effTotalPages = allRows ? Math.max(1, Math.ceil(visibleRows.length / pageSize)) : totalPages;
    const pagedRows = allRows
        ? visibleRows.slice((page - 1) * pageSize, page * pageSize)
        : visibleRows;

    const kitTabs = STATUS_TABS.map((t) => ({ ...t, count: undefined }));

    // Windowed page numbers (max 7).
    const w = 7;
    let lo = Math.max(1, page - 3);
    let hi = Math.min(effTotalPages, lo + w - 1);
    lo = Math.max(1, hi - w + 1);
    const pageNums = [];
    for (let i = lo; i <= hi; i++) pageNums.push(i);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    title={title}
                    breadcrumb={[{ label: "EPO", to: "/workflows?wf=epo" }, { label: title }]}
                    subtitle={`${subtitle}${total ? ` — ${total} loaded` : ""}`}
                    breadcrumbItems={[{ label: "EPO", href: "/epo" }, { label: title }]}
                />

                {error && <Alert color="danger" className="mt-2" toggle={() => setError(null)}>{error}</Alert>}

                {/* R38 — prop is `tabs`, not `items`. */}
                <ResponsiveTabs
                    tabs={kitTabs}
                    activeKey={activeTab}
                    onChange={setActiveTab}
                />

                <Card className="mt-3">
                    <CardBody>
                        {loading ? (
                            <div className="text-center py-5"><Spinner color="primary" /></div>
                        ) : rows.length === 0 ? (
                            <EmptyState
                                title="No EPOs to show"
                                description="Try a different status tab."
                            />
                        ) : (
                            <>

                                {/* Round 29 — exports ALL rows matching the current filters (walks the same API, all pages) */}
                                <div className="d-flex justify-content-end mb-2">
                                    <button type="button" className="btn btn-soft-primary btn-sm" disabled={!!exporting || !total} onClick={handleExportAll}>
                                        <i className="ri-download-2-line align-bottom me-1" />
                                        {exporting ? exporting : `Export all CSV (${total})`}
                                    </button>
                                    {loadingAll ? (
                                        <span className="badge bg-primary-subtle text-primary align-self-center ms-2">{loadingAll}</span>
                                    ) : allRows ? (
                                        <span className="badge bg-success-subtle text-success align-self-center ms-2" title="Sorting and filters now cover the full dataset">All {allRows.length} rows loaded</span>
                                    ) : null}
                                </div>
                                <div className="table-responsive epo-sticky-head">
                                    {/* Freeze fix: removed the conflicting `dk-sticky-thead` class. This
                                        table's wrapper already uses `epo-sticky-head` (max-height +
                                        overflow-y:auto; the header sticks to the top of the scroll box) —
                                        the same approach as the working "EPOs Ready for Payment" screen.
                                        `dk-sticky-thead` set the header to top:70px AND its `:has()` rule
                                        forced the wrapper to overflow:visible, which cancelled the scroll
                                        box and made the freeze/pagination land mid-screen. */}
                                    <table className="table table-striped table-bordered mb-0">

                                        <thead className="table-light">
                                            <tr>
                                                {columns.map((c) => (
                                                    <SortableTh key={c.id} col={c} sort={sort} onToggle={onSortToggle}
                                                        className={c.id === "approxTotal" ? "text-end"
                                                            : (c.id === "received" || c.id === "paid") ? "text-center"
                                                                : ""}>
                                                        {c.label}
                                                    </SortableTh>
                                                ))}
                                            </tr>
                                            {/* R41 — column filter row. */}
                                            <ColumnFilterRow
                                                columns={columns}
                                                filters={columnFilters}
                                                onChange={setColumnFilters}
                                            />
                                        </thead>
                                        <tbody>
                                            {visibleRows.map((r, idx) => {
                                                const epoNum = pick(r, "ePONumber", "EPONumber", "epoNumber");
                                                const createdAt = pick(r, "createdDate", "CreatedDate");
                                                const purpose = pick(r, "epoPurpose", "EpoPurpose");
                                                const createdBy = pick(r, "createdBy", "CreatedBy");
                                                const status = pick(r, "status", "Status");
                                                const totalAmt = pick(r, "approxTotal", "ApproxTotal");
                                                const received = pick(r, "received", "Received");
                                                const paid = pick(r, "paid", "Paid");
                                                return (
                                                    <tr key={epoNum ?? idx} onDoubleClick={() => epoReview.open(epoNum)} style={{ cursor: "pointer" }}>
                                                        <td><strong>{epoNum}</strong></td>
                                                        <td>{fmtDate(createdAt)}</td>
                                                        <td style={{ maxWidth: 320 }}>{purpose}</td>
                                                        <td>{createdBy}</td>
                                                        <td><StatusBadge status={status} /></td>
                                                        <td className="text-end">{fmtMoney(totalAmt)}</td>
                                                        <td className="text-center">{received ? <i className="ri-check-line text-success" /> : "—"}</td>
                                                        <td className="text-center">{paid ? <i className="ri-check-line text-success" /> : "—"}</td>
                                                        <td>
                                                            <button
                                                                type="button"
                                                                className="btn btn-link btn-sm p-0"
                                                                onClick={() => epoReview.open(epoNum)}
                                                            >View</button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {visibleRows.length === 0 && (
                                    <div className="text-muted text-center py-3 small">
                                        No rows match the current filters. Clear them or try another status tab.
                                    </div>
                                )}

                                {/* Pagination footer. */}
                                {total > 0 && (
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mt-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <span className="text-muted small">Rows per page:</span>
                                            <Input
                                                type="select"
                                                bsSize="sm"
                                                value={pageSize}
                                                onChange={(e) => changePageSize(e.target.value)}
                                                style={{ width: 80 }}
                                            >
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                                <option value={200}>200</option>
                                            </Input>
                                            <span className="text-muted small">
                                                {(allRows || visibleRows.length === rows.length)
                                                    ? <>Showing {effTotal === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, effTotal)} of {effTotal}{allRows ? " (all loaded)" : ""}</>
                                                    : <>Showing {visibleRows.length} of {rows.length} on this page (of {total} total)</>
                                                }
                                            </span>
                                        </div>
                                        <Pagination size="sm" className="mb-0">
                                            <PaginationItem disabled={page <= 1}>
                                                <PaginationLink first onClick={() => goToPage(1)} />
                                            </PaginationItem>
                                            <PaginationItem disabled={page <= 1}>
                                                <PaginationLink previous onClick={() => goToPage(page - 1)} />
                                            </PaginationItem>
                                            {pageNums.map((n) => (
                                                <PaginationItem key={n} active={n === page}>
                                                    <PaginationLink onClick={() => goToPage(n)}>{n}</PaginationLink>
                                                </PaginationItem>
                                            ))}
                                            <PaginationItem disabled={page >= effTotalPages}>
                                                <PaginationLink next onClick={() => goToPage(page + 1)} />
                                            </PaginationItem>
                                            <PaginationItem disabled={page >= effTotalPages}>
                                                <PaginationLink last onClick={() => goToPage(effTotalPages)} />
                                            </PaginationItem>
                                        </Pagination>
                                    </div>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>
            </Container>
            {epoReview.element}
        </div>
    );
};

export default MpcEpoViewer;
